import React, { useEffect, useRef, useState } from "react";
import * as faceapi from "face-api.js";
import axios from "axios";
import HealthDashboard from "./healthdashboard/HealthDashboard";
const SCAN_DURATION_MS = 5 * 1000;
const FPS = 30;
const FRAME_INTERVAL = 1000 / FPS;
function App() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [faceScan, setFaceScanData] = useState([]);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [showCanvas, setShowCanvas] = useState(true);
  const [cameraStopped, setIscameraStopped] = useState(false);
  const [scanData, setScanData] = useState({
    raw_intensity: [],
    ppg_time: [],
    average_fps: FPS,
    scanType: "face",
  });
  const lastFrameTimeRef = useRef(null);
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = process.env.PUBLIC_URL + "/models";
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
        startCamera();
      } catch {
        setError("Failed to load face detection models");
      }
    };
    loadModels();
  }, []);
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      videoRef.current.srcObject = stream;
      setScanning(true);
    } catch {
      setError("Camera access denied or not available");
    }
  };
  const stopCamera = () => {
    const stream = videoRef.current?.srcObject;
    stream?.getTracks().forEach(track => track.stop());
    setScanning(false);
  };
  useEffect(() => {
    if (!scanning) return;
    const startTime = Date.now();
    const timer = setInterval(() => {
      if (!faceDetected) return;
      const elapsed = Date.now() - startTime;
      const percent = Math.min((elapsed / SCAN_DURATION_MS) * 100, 100);
      setProgress(percent);


      if (percent >= 100) {
        setFaceDetected(false);
        setFaceDetected(true);
        setError("");
        setIscameraStopped(true);
        setShowCanvas(false);
        stopCamera();
        clearInterval(timer);
        faceExternalApi();

      }
    }, 1000);
    return () => clearInterval(timer);
  }, [scanning, faceDetected]);
  const analyzeFrame___old = async () => {
    if (!videoRef.current || !cameraReady) return;
    if (!canvasRef.current) return;
    if (videoRef.current.videoWidth === 0) return;
    const detection = await faceapi
      .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!detection) {
      setFaceDetected(false);
      setError("Face not detected. Please stay in frame.");
      return;
    }
    setError("");
    setFaceDetected(true);
    const landmarks = detection.landmarks;
    if (landmarks) {
      /*       const points = landmarks.positions.map(p => [p.x, p.y]);
            const hull = convexHull(points); */
      const points = landmarks.positions.map(p => [p.x, p.y]);
      // eyebrow landmarks (forehead reference)
      const leftBrow = landmarks.getLeftEyeBrow();
      const rightBrow = landmarks.getRightEyeBrow();
      // compute forehead points above eyebrows
      const foreheadPoints = [...leftBrow, ...rightBrow].map(p => [
        p.x,
        p.y - (detection.detection.box.height * 0.25) // push upward
      ]);
      // merge points
      const allPoints = [
        ...points,
        ...foreheadPoints.map(p => ({ x: p[0], y: p[1] }))
      ].map(p => [p.x || p[0], p.y || p[1]]);
      // convex hull of full face
      const hull = convexHull(allPoints);
      // Draw futuristic neon face outline
      ctx.strokeStyle = "rgba(0, 255, 255, 0.9)";
      ctx.lineWidth = 3;
      ctx.shadowColor = "#00fff7";
      ctx.shadowBlur = 18;
      ctx.beginPath();
      hull.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.closePath();
      ctx.stroke();
      // Optional: animated scanning pulse
      ctx.beginPath();
      const time = performance.now() * 0.005;
      hull.forEach(([x, y], i) => {
        const offsetX = 5 * Math.sin(time + i);
        const offsetY = 5 * Math.cos(time + i);
        if (i === 0) ctx.moveTo(x + offsetX, y + offsetY);
        else ctx.lineTo(x + offsetX, y + offsetY);
      });
      ctx.closePath();
      ctx.strokeStyle = "rgba(0,255,255,0.3)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    // Collect RGB data from bounding box
    // const { x, y, width, height } = detection.detection.box;
    const { x, y, width, height } = detection.detection.box;
    const foreheadHeight = height * 0.25;
    const roi = {
      x,
      y: y - foreheadHeight,
      width,
      height: foreheadHeight
    };
    // const imageData = ctx.getImageData(x, y, width, height).data;
    const imageData = ctx.getImageData(roi.x, roi.y, roi.width, roi.height).data;
    let r = 0, g = 0, b = 0;
    const pixels = imageData.length / 4;
    for (let i = 0; i < imageData.length; i += 4) {
      r += imageData[i];
      g += imageData[i + 1];
      b += imageData[i + 2];
    }
    setScanData(prev => ({
      ...prev,
      raw_intensity: [...prev.raw_intensity, { r: r / pixels, g: g / pixels, b: b / pixels }],
      ppg_time: [...prev.ppg_time, performance.now()],
    }));
  };
  const analyzeFrame = async () => {
    if (!videoRef.current || !cameraReady) return;
    if (!canvasRef.current) return;
    if (videoRef.current.videoWidth === 0) return;
    const detection = await faceapi
      .detectSingleFace(
        videoRef.current,
        new faceapi.TinyFaceDetectorOptions()
      )
      .withFaceLandmarks();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    /* working */
    if (!detection) {
      if (progress === 100) {
        setFaceDetected(true);
        setError("");
        return
      }
      console.log(progress);

      setFaceDetected(false);
      setError("Face not detected. Stay in frame.");
      return;
    }
    setFaceDetected(true);
    setError("");
    const { x, y, width, height } = detection.detection.box;
    /* ---------- FACE OVAL ---------- */
    const centerX = x + width / 2;
    const centerY = y + height * 0.45;
    const radiusX = width * 0.42;
    const radiusY = height * 0.62;
    const t = performance.now() * 0.004;
    ctx.strokeStyle = "#00ffff";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#00ffff";
    ctx.shadowBlur = 12 + Math.sin(t) * 4;
    ctx.beginPath();
    ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
    ctx.stroke();
    /* ---------- SCAN LINE ---------- */
    const scanY =
      centerY - radiusY + ((Math.sin(t * 2) + 1) / 2) * radiusY * 2;
    ctx.beginPath();
    ctx.moveTo(centerX - radiusX * 0.8, scanY);
    ctx.lineTo(centerX + radiusX * 0.8, scanY);
    ctx.strokeStyle = "rgba(0,255,255,0.6)";
    ctx.lineWidth = 2;
    ctx.stroke();
    /* ---------- CORNER BRACKETS ---------- */
    const bracketSize = 20;
    const left = centerX - radiusX;
    const right = centerX + radiusX;
    const top = centerY - radiusY;
    const bottom = centerY + radiusY;
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#00ffff";
    // top left
    ctx.beginPath();
    ctx.moveTo(left, top + bracketSize);
    ctx.lineTo(left, top);
    ctx.lineTo(left + bracketSize, top);
    ctx.stroke();
    // top right
    ctx.beginPath();
    ctx.moveTo(right - bracketSize, top);
    ctx.lineTo(right, top);
    ctx.lineTo(right, top + bracketSize);
    ctx.stroke();
    // bottom left
    ctx.beginPath();
    ctx.moveTo(left, bottom - bracketSize);
    ctx.lineTo(left, bottom);
    ctx.lineTo(left + bracketSize, bottom);
    ctx.stroke();
    // bottom right
    ctx.beginPath();
    ctx.moveTo(right - bracketSize, bottom);
    ctx.lineTo(right, bottom);
    ctx.lineTo(right, bottom - bracketSize);
    ctx.stroke();
    /* ---------- RGB COLLECTION ---------- */
    const imageData = ctx.getImageData(x, y, width, height).data;
    let r = 0, g = 0, b = 0;
    const pixels = imageData.length / 4;
    for (let i = 0; i < imageData.length; i += 4) {
      r += imageData[i];
      g += imageData[i + 1];
      b += imageData[i + 2];
    }
    setScanData(prev => ({
      ...prev,
      raw_intensity: [
        ...prev.raw_intensity,
        { r: r / pixels, g: g / pixels, b: b / pixels }
      ],
      ppg_time: [...prev.ppg_time, performance.now()]
    }));
  };
  function convexHull(points) {
    points.sort(([ax, ay], [bx, by]) => ax === bx ? ay - by : ax - bx);
    const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const lower = [];
    for (let p of points) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
      lower.push(p);
    }
    const upper = [];
    for (let i = points.length - 1; i >= 0; i--) {
      const p = points[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
      upper.push(p);
    }
    upper.pop();
    lower.pop();
    return lower.concat(upper);
  }
  const faceExternalApi = async () => {
    try {
      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: "https://prd.server.watchyourhealth.com/apis/s/face-vital-scan",
        headers: {
          "accept": "application/json",
          "accept-language": "en-GB,en;q=0.9",
          "content-type": "application/json; charset=utf-8",
          "x-api-key": "ra-O4uLYw1k2DMJB16FRL6JCDUMvJz3oCjU",
        },
        data: scanData,
      };
      const response = await axios.request(config);
      setFaceScanData(response.data);
    } catch (err) {
      console.log(err);
    }
  };
  /* ---------------- Frame Loop ---------------- */
  useEffect(() => {
    if (!cameraReady) return;
    const interval = setInterval(analyzeFrame, FRAME_INTERVAL);
    return () => clearInterval(interval);
  }, [cameraReady, scanning]);
  useEffect(() => {
    return () => stopCamera();
  }, []);
  /* ---------------- UI ---------------- */
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: `
    radial-gradient(circle at 20% 30%, rgba(0,255,255,0.08), transparent 40%),
    radial-gradient(circle at 80% 70%, rgba(0,150,255,0.08), transparent 40%),
    linear-gradient(135deg, #050510, #0d0d1a, #111124)
  `,
        padding: 20,
        fontFamily: "Inter, system-ui, sans-serif"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "rgba(18,18,35,0.65)",
          borderRadius: 16,
          padding: 28,
          textAlign: "center",
          backdropFilter: "blur(25px)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: `
    0 20px 60px rgba(0,0,0,0.6),
    0 0 0 1px rgba(0,255,255,0.05),
    0 0 40px rgba(0,255,255,0.08)
  `,
        }}
      >
        <h2 style={{
          marginBottom: 6,
          color: "#f2f6ff",
          fontWeight: 600,
          letterSpacing: 0.3,
          fontSize: 22,
          display: showCanvas ? "block" : "none"
        }}>
          Face Scan
        </h2>
        <p
          style={{
            fontSize: 14,
            color: "#9ca3af",
            marginBottom: 16,
            display: showCanvas ? "block" : "none"
          }}
        >
          Keep your face inside the frame
        </p>
        {/* Video + Canvas */}
        <div
          style={{
            position: "relative",
            borderRadius: 14,
            overflow: "hidden",
            border: "1px solid rgba(0,255,255,0.25)",
            background: "#000",
            boxShadow: `
    inset 0 0 40px rgba(0,255,255,0.15),
    0 0 30px rgba(0,255,255,0.08)
  `,
            display: showCanvas ? "block" : "none",
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            onLoadedMetadata={() => setCameraReady(true)}
            style={{
              width: "100%",
              height: 300,
              objectFit: "cover",
              background: "#000"
            }}
          />
          <canvas
            ref={canvasRef}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: 280,
              pointerEvents: "none",
            }}
          />
        </div>
        {/* Progress */}
        {scanning && (
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                width: "100%",
                height: 20,
                objectFit: "cover",
                background: "#000"
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  background:
                    "linear-gradient(90deg,#00f5ff,#00b3ff,#3b82f6)",
                  boxShadow: "0 0 12px rgba(0,255,255,0.6)",
                  transition: "width 0.3s linear",
                }}
              />
            </div>
            <div style={{
              fontSize: 12,
              color: "#6ee7ff",
              marginTop: 6
            }}>
              {Math.floor(progress)}% completed
            </div>
          </div>
        )}
        {/* Error */}
        {error && (
          <div
            style={{
              background: "rgba(255,60,60,0.08)",
              color: "#ff6b6b",
              padding: "10px 14px",
              borderRadius: 10,
              fontSize: 13,
              marginTop: 12,
              border: "1px solid rgba(255,60,60,0.2)"
            }}
          >
            ⚠️ {error}
          </div>
        )}
        {/* Health Dashboard */}
        {!showCanvas && <HealthDashboard faceScan={faceScan} />}
      </div>
    </div>
  );
}
export default App;
